import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { handleServerFormError, InternalServerError, ValidationError } from '#api/errors.ts';
import { useAddRole } from '#api/roles.queries.ts';
import { NEW_ROLE_FLAG, NO_ROLE_FLAG } from '#components/patterns/entity-select/role/option-constants.ts';
import type { HostFormData, HostFormTransformedData } from '#screens/hosts/form/host-form-schema.ts';
import { useUpdateRoleCache } from '#screens/roles/detail/form/RoleDetailForm.queries.ts';
import { makeMapRoleErrorsToForm } from '../mapErrorsToForm.ts';

/**
 * This hook is used in forms which can create a new role as part of host creation.  If a role has already
 * been created, we'll return its id.  Otherwise, if we need to make a new role, we'll fire the mutation and
 * return the new role's ID.
 */
export function useCreatableRole(formContext: UseFormReturn<HostFormData, unknown, HostFormTransformedData>) {
  const [createdRoleId, setCreatedRoleId] = useState<string | undefined>();
  const addRole = useAddRole();
  const updateRoleCache = useUpdateRoleCache();
  const { setError } = formContext;

  const createOrUseExistingRole = async (data: HostFormTransformedData) => {
    // We got partway through before, and we don't allow changing after that, so use the saved ID
    if (createdRoleId) {
      return createdRoleId;
    }

    if (data.roleID === NO_ROLE_FLAG) {
      return null;
    }

    if (data.roleID === NEW_ROLE_FLAG && data.roleName) {
      try {
        const newRole = await addRole.mutateAsync({
          name: data.roleName,
          firewallRules: [
            {
              protocol: 'ICMP',
              portRange: null,
              description: 'Allows ping requests from other hosts',
              allowedRoleID: null,
            },
          ],
        });
        updateRoleCache(newRole);
        setCreatedRoleId(newRole.id);
        return newRole.id;
      } catch (error) {
        if (error instanceof ValidationError) {
          handleServerFormError(error, makeMapRoleErrorsToForm(setError));
          return;
        }
        if (error instanceof InternalServerError) {
          setError('root.global', { message: 'We couldn’t add the new role, please try again' });
          return;
        }
      }
    }

    return data.roleID;
  };

  return { createOrUseExistingRole, hasCreatedRole: Boolean(createdRoleId) };
}
